import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { AdminPostItemFragment } from '../../../api/client'
import { faIconName } from '../../../util/classNames'
import ModerationBreakdown from './ModerationBreakdown'
import { highlightMatches } from './highlightMatches'
import { postCollectionNote, postPermalink } from './postPermalink'

type Props = {
    posts: AdminPostItemFragment[]
    /** The title search in force, so matching text can be marked in each row. */
    highlight?: string
}

export function formatPublishTime(iso: string): string {
    return dayjs(iso).format('D MMM YYYY, HH:mm')
}

/**
 * The post list. Server-paginated, so a plain table rather than tanstack —
 * but the same chrome as SimpleTable so the admin lists read as one family.
 * The whole row links to the post's comments; the external-link icon is the
 * one control that goes elsewhere.
 */
export default function PostTable({ posts, highlight }: Props) {
    return (
        <div className="overflow-x-auto rounded-lg border border-ink-200 bg-white shadow-sm">
            <table className="min-w-full table-fixed border-collapse text-sm">
                <thead className="bg-ink-50">
                    <tr className="border-b border-ink-200 text-left text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                        <th className="w-[34%] px-4 py-2">Post</th>
                        <th className="w-[18%] px-4 py-2">Profile</th>
                        <th className="w-[13%] px-4 py-2">Published</th>
                        <th className="w-[10%] px-4 py-2 text-right">Comments</th>
                        <th className="w-[21%] px-4 py-2">Moderation</th>
                        <th className="w-[4%] px-4 py-2">
                            <span className="sr-only">Open on platform</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-100 bg-white">
                    {posts.map((post) => (
                        <PostRow key={post.id} post={post} highlight={highlight} />
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// Tooltips go through the app-wide react-tooltip instance mounted in Main.tsx
// (id "tooltip-id"), as the content cards do. A native title attribute was
// tried first and never appeared reliably here.
function PostRow({ post, highlight }: { post: AdminPostItemFragment; highlight?: string }) {
    const title = post.title.trim() || 'Untitled post'
    const collected = post.collectedCommentCount
    // The platform's own count, where the fetcher records one. Shown small
    // beneath ours because a gap between the two is exactly what an admin
    // opening this page is usually looking for.
    const platformCount = post.platformCommentCount
    const permalink = postPermalink(post)
    const collectionNote = postCollectionNote(post.socialMediaServiceShortName)

    return (
        <tr className="group hover:bg-ink-50">
            <td className="px-4 py-2.5 align-top">
                <Link
                    to={`/admin/post/${post.id}`}
                    className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500 rounded"
                >
                    <div className="flex items-start gap-2.5">
                        <i
                            className={`fab ${faIconName(post.socialMediaServiceShortName, true)} mt-0.5 text-base`}
                            aria-label={post.socialMediaServiceShortName}
                        />
                        <div className="min-w-0">
                            {/* Titles are the post's caption and can run to a paragraph, so
                                they wrap rather than truncate: an admin scanning for a post
                                needs to read them. The labels sit on the last line's baseline. */}
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span className="break-words text-sm font-medium text-ink-900 group-hover:text-arwen-600">
                                    <HighlightedText text={title} query={highlight} />
                                </span>
                                <PostTypeLabel isAd={post.isAd} />
                                {post.isDeleted && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">
                                        Deleted
                                    </span>
                                )}
                            </div>
                            <div className="truncate text-xs text-ink-500">
                                ID {post.id} · {post.servicePostId}
                            </div>
                        </div>
                    </div>
                </Link>
            </td>
            <td className="px-4 py-2.5 align-top">
                <div className="truncate text-sm text-ink-900">
                    {post.accountName || post.serviceAccountUsername || `Profile ${post.accountId}`}
                </div>
                <div className="truncate text-xs text-ink-500">{post.teamName ?? 'No team'}</div>
            </td>
            <td className="px-4 py-2.5 align-top whitespace-nowrap tabular-nums">
                <div className="text-sm text-ink-700">{formatPublishTime(post.publishTime)}</div>
                {/* When we first ingested it, under when the platform published it,
                    so the gap between the two reads as how late collection started. */}
                <div className="text-xs text-ink-500">
                    Created {formatPublishTime(post.createdAt)}
                </div>
            </td>
            <td className="px-4 py-2.5 align-top text-right tabular-nums">
                <div className="flex items-center justify-end gap-1 text-sm font-medium text-ink-900">
                    {/* The warning sits on the count it undermines, not on the title:
                        an admin reading this column is the one who needs to know the
                        figure is stale. */}
                    {collectionNote && (
                        <span
                            data-tooltip-id="tooltip-id"
                            data-tooltip-content={collectionNote}
                            className="inline-flex cursor-help text-suspect"
                        >
                            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">{collectionNote}</span>
                        </span>
                    )}
                    {collected.toLocaleString('en-GB')}
                </div>
                {platformCount !== null && platformCount !== undefined && (
                    <div
                        className="cursor-help text-xs text-ink-500"
                        data-tooltip-id="tooltip-id"
                        data-tooltip-content="The platform's own count when we last polled it. It leaves out comments hidden or deleted since, and lags collection, so it will not match ours exactly."
                    >
                        of {platformCount.toLocaleString('en-GB')}
                    </div>
                )}
            </td>
            <td className="px-4 py-2.5 align-top">
                <ModerationBreakdown
                    collected={collected}
                    safe={post.safeCount}
                    suspect={post.suspectCount}
                    severe={post.severeCount}
                />
            </td>
            <td className="px-4 py-2.5 align-top text-right">
                {permalink && (
                    <a
                        href={permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex rounded p-1 text-ink-400 hover:text-arwen-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500"
                        title="Open on platform"
                    >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Open on platform</span>
                    </a>
                )}
            </td>
        </tr>
    )
}

/** The design system's ghost `ORGANIC POST` / `PAID AD` label, in miniature. */
export function PostTypeLabel({ isAd }: { isAd: boolean }) {
    return (
        <span className="flex-none rounded border border-ink-200 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-ink-500">
            {isAd ? 'Paid ad' : 'Organic'}
        </span>
    )
}

/**
 * Marks the parts of a title that matched the search. Ochre rather than red
 * so a highlight never reads as a Severe flag, and no colour change on the
 * text so it stays legible against the row hover.
 */
export function HighlightedText({ text, query }: { text: string; query?: string }) {
    return (
        <>
            {highlightMatches(text, query).map((run, i) =>
                run.match ? (
                    <mark key={i} className="rounded-sm bg-suspect-100 px-0.5 text-inherit">
                        {run.text}
                    </mark>
                ) : (
                    <span key={i}>{run.text}</span>
                )
            )}
        </>
    )
}
